import { useCallback, useRef } from "react";
import { BatteryCharging } from "lucide-react";
import type { BatteryOutlook, Reading } from "@spf/contracts";
import { useLiveTelemetry } from "../lib/useLiveTelemetry.js";
import { useFreshness } from "../lib/useFreshness.js";
import { Card } from "../components/Card.js";
import { Freshness } from "../components/Freshness.js";
import { Stat } from "../components/widgets/Stat.js";
import { ChargeCard } from "../components/battery/ChargeCard.js";
import { DispatchCard } from "../components/battery/DispatchCard.js";
import { OutagesCard } from "../components/battery/OutagesCard.js";
import { ReserveCard } from "../components/battery/ReserveCard.js";
import { ThermalCard } from "../components/battery/ThermalCard.js";
import { Empty, Failed, Loading } from "../components/ViewState.js";
import { formatUnit } from "../lib/format.js";

/**
 * The battery on its own screen, in the design's two-column diagnostics
 * layout: the banner and its quick-stat rail, then charge and dispatch down
 * the wide side with the reserve control and thermal map beside them.
 *
 * The one question this installation actually asks -- "how long will the
 * battery carry the house" -- is the third of the jobs in `REQUIREMENTS.md`
 * section 1, and it is the reason this page is more than a repeat of the
 * dashboard's battery panel: it shows the numbers the answer is made from, so
 * a reader can see why the answer is what it is. The job's second half, the
 * history of outages and how much of each the battery covered, is the last
 * card, full width because each row carries notes as well as figures.
 *
 * Every derived figure here is computed on the server
 * (`apps/api/src/telemetry/battery.ts`) and arrives with the reading. Section
 * 3 forbids business rules in the client, and autonomy is a business rule: it
 * depends on a capacity the inverter never reports and on a reserve floor the
 * operator chose. This file formats; it does not decide.
 */
export function BatteryPage({ canWrite }: { canWrite: boolean }) {
  const { latest, retry } = useLiveTelemetry();
  const heading = useRef<HTMLHeadingElement>(null);
  const freshness = useFreshness(latest);

  const retryHere = useCallback(() => {
    retry();
    heading.current?.focus();
  }, [retry]);

  return (
    <section aria-labelledby="battery-heading" className="flex flex-col gap-5">
      <div>
        <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          <BatteryCharging size={14} aria-hidden="true" className="text-accent-ink" />
          Storage diagnostics
        </p>
        <h2
          id="battery-heading"
          ref={heading}
          tabIndex={-1}
          className="text-3xl font-bold tracking-tight text-ink outline-none sm:text-4xl"
        >
          Battery
        </h2>
      </div>

      {latest !== null && latest.reading !== null && (
        <Freshness value={latest} freshness={freshness} />
      )}

      {latest === null ? (
        <Card title="Connection">
          <Loading label="Connecting for live updates…" />
        </Card>
      ) : latest.reading === null ? (
        <Card title="Connection">
          <Empty>
            No telemetry yet. Once the inverter&rsquo;s dongle reports, this page updates on its own
            within about five minutes.
          </Empty>
        </Card>
      ) : (
        <>
          <QuickStats reading={latest.reading} outlook={latest.battery} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="flex flex-col gap-4 lg:col-span-2">
              <ChargeCard reading={latest.reading} outlook={latest.battery} />
              <DispatchCard />
            </div>
            <div className="flex flex-col gap-4">
              <OutlookCard outlook={latest.battery} onRetry={retryHere} />
              <ReserveCard current={latest.battery?.reserveSocPct ?? null} canWrite={canWrite} />
              <ThermalCard reading={latest.reading} />
            </div>
          </div>
          <OutagesCard refreshKey={latest.reading.time} />
        </>
      )}
    </section>
  );
}

/**
 * The design's stat rail beside the title: three figures, divided.
 *
 * The design's three are state of charge, state of health and cycle count.
 * The last two are not reported by this hardware and no later slice can
 * produce them from what it does send, so the rail carries runtime instead --
 * which is the figure this installation was built to answer.
 */
function QuickStats({ reading, outlook }: { reading: Reading; outlook: BatteryOutlook | null }) {
  return (
    // A plain row, not a `<dl>`: the dividers between the figures are
    // `<span>`s, and a definition list may hold nothing but `dt`, `dd` and
    // `div`. axe reports that as a `definition-list` violation.
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-line bg-card p-2">
      <Pill label="State of charge" value={formatUnit(reading.socPct, "%")} accent />
      <Divider />
      <Pill
        label="Usable now"
        value={outlook === null ? "Not configured" : formatUnit(outlook.usableKwh, "kWh")}
      />
      <Divider />
      <Pill
        label="Runtime left"
        value={
          outlook === null || outlook.autonomyHours === null
            ? "Nothing drawing"
            : formatUnit(outlook.autonomyHours, "h")
        }
      />
    </div>
  );
}

function Pill({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <p className="px-3 py-1 text-center">
      <span className="block text-[11px] text-ink-muted">{label}</span>
      <span
        className={
          accent
            ? "tabular text-base font-bold text-accent-ink"
            : "tabular text-base font-bold text-ink"
        }
      >
        {value}
      </span>
    </p>
  );
}

function Divider() {
  return <span aria-hidden="true" className="h-8 w-px bg-line" />;
}

function OutlookCard({
  outlook,
  onRetry,
}: {
  outlook: BatteryOutlook | null;
  onRetry: () => void;
}) {
  if (outlook === null) {
    return (
      <Card title="How long it lasts">
        {/* Not an error and not a zero: the outlook is absent only when the
            instance has no capacity configured, and inventing hours from a
            capacity nobody supplied is the one thing this page must not do. */}
        <Failed
          message="Battery capacity is not configured, so runtime cannot be worked out. Set it on the Settings page."
          onRetry={onRetry}
        />
      </Card>
    );
  }

  return (
    <Card title="How long it lasts">
      {/* A plain grid, not a `<dl>`. `Stat` renders `div > span`, which is not
          valid inside a definition list and which axe reports as a
          `definition-list` violation -- it caught exactly this once already. */}
      <div className="grid grid-cols-2 gap-6">
        <Stat
          label="Runtime left"
          // Null is rendered as words, not as a dash and not as a large
          // number. Nothing drawing means the honest answer is unbounded, and
          // picking a ceiling would be inventing a measurement.
          value={outlook.autonomyHours === null ? "—" : formatUnit(outlook.autonomyHours, "h")}
          note={
            outlook.autonomyHours === null
              ? "Nothing is drawing from the battery."
              : `At the load right now, down to the ${String(outlook.reserveSocPct)}% reserve.`
          }
          tone="accent"
        />
        <Stat
          label="Time to full"
          value={outlook.timeToFullHours === null ? "—" : formatUnit(outlook.timeToFullHours, "h")}
          note={outlook.timeToFullHours === null ? "Not charging." : "At the present charge rate."}
        />
        <Stat
          label="Usable energy"
          value={formatUnit(outlook.usableKwh, "kWh")}
          note={`Above the ${String(outlook.reserveSocPct)}% reserve.`}
        />
        <Stat
          label="Capacity"
          value={formatUnit(outlook.capacityKwh, "kWh")}
          note="Configured, not measured. The inverter does not report it."
        />
      </div>
    </Card>
  );
}
